import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  XMarkIcon,
  UserIcon,
  PhoneIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { marketplaceApi } from '../../api/marketplace';
import { customersApi, Customer, CustomerStatus } from '../../api/customers';
import { birdsApi, BirdStatus } from '../../api/birds';
import { accountsApi, AccountSubType } from '../../api/accounts';
import { journalApi } from '../../api/journal';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalConfig } from '../../contexts/GlobalConfigContext';
import toast from 'react-hot-toast';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface OrderItem {
  birdId: string;
  birdName: string;
  species: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

interface NewOrderData {
  orderSource: 'local' | 'marketplace' | 'whatsapp' | 'facebook' | 'instagram' | 'telegram' | 'custom';
  buyer: {
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
    customerId?: string; // Add customer ID for integration
  };
  items: OrderItem[];
  subtotal: number;
  tax: number;
  shippingCost: number;
  totalAmount: number;
  notes?: {
    buyerNotes: string;
    internalNotes: string;
  };
}

export default function NewOrderForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { config } = useGlobalConfig();
  const [loading, setLoading] = useState(false);
  const [birds, setBirds] = useState<any[]>([]);
  const [selectedBirds, setSelectedBirds] = useState<OrderItem[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [orderData, setOrderData] = useState<NewOrderData>({
    orderSource: 'local',
    buyer: {
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
    },
    items: [],
    subtotal: 0,
    tax: 0,
    shippingCost: 0,
    totalAmount: 0,
    notes: {
      buyerNotes: '',
      internalNotes: '',
    },
  });

  const facilityId = 'default'; // Use a default facility ID, backend will handle facility resolution

  useEffect(() => {
    loadBirds();
    loadCustomers();
  }, []);

  useEffect(() => {
    calculateTotals();
  }, [selectedBirds]);

  const loadBirds = async () => {
    try {
      // Load available birds for sale
      const response = await marketplaceApi.getListings(facilityId, { status: 'published' });
      const availableBirds = response.listings || [];
      setBirds(availableBirds);
    } catch (error) {
      console.error('Error loading birds:', error);
      toast.error('Failed to load available birds');
    }
  };

  const loadCustomers = async () => {
    try {
      setLoadingCustomers(true);
      
      // Load active customers for order integration
      const customersData = await customersApi.getAll();
      const activeCustomers = customersData.filter((customer: Customer) => 
        customer.status === CustomerStatus.ACTIVE
      );
      
      setCustomers(activeCustomers);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const selectCustomer = (customer: Customer) => {
    setOrderData(prev => ({
      ...prev,
      buyer: {
        ...prev.buyer,
        customerId: customer.id,
        name: customer.name,
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        city: customer.city || '',
        state: customer.province || '',
        country: customer.country || '',
        postalCode: customer.postalCode || '',
      }
    }));
    setShowCustomerSelector(false);
    setCustomerSearchTerm('');
    toast.success(`Customer ${customer.name} selected`);
  };

  const calculateTotals = () => {
    const subtotal = selectedBirds.reduce((sum, item) => sum + item.totalPrice, 0);
    const tax = subtotal * 0.15; // 15% tax rate
    const shippingCost = subtotal > 1000 ? 0 : 50; // Free shipping over $1000
    const totalAmount = subtotal + tax + shippingCost;

    setOrderData(prev => ({
      ...prev,
      items: selectedBirds,
      subtotal,
      tax,
      shippingCost,
      totalAmount,
    }));
  };

  const addBirdToOrder = (bird: any) => {
    const existingItem = selectedBirds.find(item => item.birdId === bird.id);
    
    if (existingItem) {
      setSelectedBirds(prev => prev.map(item => 
        item.birdId === bird.id 
          ? { ...item, quantity: item.quantity + 1, totalPrice: (item.quantity + 1) * item.unitPrice }
          : item
      ));
    } else {
      const newItem: OrderItem = {
        birdId: bird.id,
        birdName: bird.title,
        species: bird.specifications?.species || 'Unknown',
        quantity: 1,
        unitPrice: bird.price?.amount || 0,
        totalPrice: bird.price?.amount || 0,
      };
      setSelectedBirds(prev => [...prev, newItem]);
    }
  };

  const updateItemQuantity = (birdId: string, quantity: number) => {
    if (quantity <= 0) {
      setSelectedBirds(prev => prev.filter(item => item.birdId !== birdId));
    } else {
      setSelectedBirds(prev => prev.map(item => 
        item.birdId === birdId 
          ? { ...item, quantity, totalPrice: quantity * item.unitPrice }
          : item
      ));
    }
  };

  const removeItem = (birdId: string) => {
    setSelectedBirds(prev => prev.filter(item => item.birdId !== birdId));
  };

  const handleOrderComplete = async () => {
    try {
      setLoading(true);
      
      // 1. Update bird status to SOLD
      for (const item of selectedBirds) {
        try {
          await birdsApi.update(item.birdId, { status: BirdStatus.SOLD });
        } catch (error) {
          console.error(`Failed to update bird ${item.birdId} status:`, error);
        }
      }
      
      // 2. Create accounting journal entry for the sale
      const journalCreated = await createSaleTransaction();
      
      // 3. Update customer purchase history if customer is selected
      if (orderData.buyer.customerId) {
        await updateCustomerPurchaseHistory();
      }
      
      toast.success(
        journalCreated
          ? 'Order completed successfully! Bird statuses updated and journal entry posted.'
          : 'Order completed. Bird statuses updated (journal entry skipped — check accounting accounts).'
      );
      navigate('/marketplace/orders');
    } catch (error) {
      console.error('Error completing order:', error);
      toast.error('Failed to complete order');
    } finally {
      setLoading(false);
    }
  };

  const createSaleTransaction = async (): Promise<boolean> => {
    if (orderData.totalAmount <= 0) {
      return false;
    }

    try {
      const [revenueAccounts, cashAccounts, bankAccounts] = await Promise.all([
        accountsApi.getAccounts({ subType: AccountSubType.OPERATING_REVENUE, isActive: true }),
        accountsApi.getAccounts({ subType: AccountSubType.CASH, isActive: true }),
        accountsApi.getAccounts({ subType: AccountSubType.BANK, isActive: true }),
      ]);

      const salesAccount =
        revenueAccounts.find(
          (account) =>
            account.name.toLowerCase().includes('sales') ||
            account.name.toLowerCase().includes('revenue'),
        ) ?? revenueAccounts[0];

      const cashAccount = cashAccounts[0] ?? bankAccounts[0];

      if (!salesAccount || !cashAccount) {
        console.warn('Missing sales revenue or cash/bank account for journal entry');
        return false;
      }

      const reference = `SALE-${Date.now()}`;
      const birdNames = selectedBirds.map((item) => item.birdName).join(', ');
      const lineItems = [
        {
          accountId: cashAccount.id,
          debitAmount: orderData.totalAmount,
          creditAmount: 0,
          description: 'Cash received from marketplace order',
          reference,
        },
        {
          accountId: salesAccount.id,
          debitAmount: 0,
          creditAmount: orderData.totalAmount,
          description: `Revenue from sale of ${selectedBirds.length} bird(s)`,
          reference,
        },
      ];

      const entry = await journalApi.createJournalEntry({
        date: new Date().toISOString(),
        description: `Marketplace sale: ${birdNames}`,
        reference,
        source: 'sales',
        lineItems,
        notes: orderData.buyer.name
          ? `Buyer: ${orderData.buyer.name}${orderData.buyer.customerId ? ` (customer ${orderData.buyer.customerId})` : ''}`
          : undefined,
      });

      await journalApi.postJournalEntry(entry.id);
      return true;
    } catch (error) {
      console.error('Failed to create journal entry for sale:', error);
      toast.error('Sale recorded, but journal entry could not be created');
      return false;
    }
  };

  const updateCustomerPurchaseHistory = async () => {
    try {
      if (!orderData.buyer.customerId) return;
      
      // Update customer metadata with purchase history
      const customer = customers.find(c => c.id === orderData.buyer.customerId);
      if (!customer) return;
      
      const updatedMetadata = {
        ...customer.metadata,
        purchaseHistory: [
          ...(customer.metadata?.purchaseHistory || []),
          {
            date: new Date().toISOString(),
            amount: orderData.totalAmount,
            description: `Purchase of ${selectedBirds.length} bird(s)`
          }
        ],
        totalPurchases: (customer.metadata?.totalPurchases || 0) + 1,
        totalSpent: (customer.metadata?.totalSpent || 0) + orderData.totalAmount,
        lastPurchaseDate: new Date().toISOString(),
        averageOrderValue: ((customer.metadata?.totalSpent || 0) + orderData.totalAmount) / 
                          ((customer.metadata?.totalPurchases || 0) + 1)
      };
      
      await customersApi.update(orderData.buyer.customerId, {
        metadata: updatedMetadata
      });
      
    } catch (error) {
      console.error('Error updating customer purchase history:', error);
      toast.error('Failed to update customer history');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: config?.currency?.default || 'ZAR',
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-slate-50/80 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/marketplace/orders')}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80"
              >
                <ChevronLeftIcon className="h-4 w-4 mr-2" />
                Back to Orders
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Create New Order</h1>
                <p className="text-sm text-slate-500">Add items and customer information to create a new order</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Available Birds */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-slate-900">Available Birds</h2>
                <p className="text-sm text-slate-500">Select birds to add to the order</p>
              </div>
              <div className="p-6">
                {birds.length === 0 ? (
                  <div className="text-center py-8">
                    <ShoppingCartIcon className="mx-auto h-12 w-12 text-slate-400" />
                    <h3 className="mt-2 text-sm font-medium text-slate-900">No birds available</h3>
                    <p className="mt-1 text-sm text-slate-500">No published listings found.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {birds.map((bird) => (
                      <div key={bird.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-sm font-medium text-slate-900">{bird.title}</h3>
                          <span className="text-sm font-semibold text-slate-900">
                            {formatCurrency(bird.price?.amount || 0)}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mb-3">
                          {bird.specifications?.species || 'Unknown species'}
                        </p>
                        <button
                          onClick={() => addBirdToOrder(bird)}
                          className="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700"
                        >
                          <PlusIcon className="h-4 w-4 mr-2" />
                          Add to Order
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg sticky top-8">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-slate-900">Order Summary</h2>
              </div>
              <div className="p-6">
                {selectedBirds.length === 0 ? (
                  <div className="text-center py-8">
                    <ShoppingCartIcon className="mx-auto h-8 w-8 text-slate-400" />
                    <p className="mt-2 text-sm text-slate-500">No items selected</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedBirds.map((item) => (
                      <div key={item.birdId} className="flex items-center justify-between py-2 border-b border-gray-100">
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-slate-900">{item.birdName}</h4>
                          <p className="text-xs text-slate-500">{item.species}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <button
                              onClick={() => updateItemQuantity(item.birdId, item.quantity - 1)}
                              className="text-slate-400 hover:text-slate-600"
                            >
                              -
                            </button>
                            <span className="text-sm text-slate-900">{item.quantity}</span>
                            <button
                              onClick={() => updateItemQuantity(item.birdId, item.quantity + 1)}
                              className="text-slate-400 hover:text-slate-600"
                            >
                              +
                            </button>
                            <button
                              onClick={() => removeItem(item.birdId)}
                              className="ml-2 text-red-400 hover:text-red-600"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-slate-900">
                            {formatCurrency(item.totalPrice)}
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatCurrency(item.unitPrice)} each
                          </p>
                        </div>
                      </div>
                    ))}

                    <div className="border-t pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Subtotal:</span>
                        <span className="font-medium">{formatCurrency(orderData.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Tax (15%):</span>
                        <span className="font-medium">{formatCurrency(orderData.tax)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Shipping:</span>
                        <span className="font-medium">{formatCurrency(orderData.shippingCost)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-semibold border-t pt-2">
                        <span>Total:</span>
                        <span>{formatCurrency(orderData.totalAmount)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Customer Information Form */}
        <div className="mt-8">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-slate-900">Customer Information</h2>
            </div>
            <form onSubmit={handleOrderComplete} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Order Source */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Order Source
                  </label>
                  <select
                    value={orderData.orderSource}
                    onChange={(e) => setOrderData(prev => ({ ...prev, orderSource: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="local">Local</option>
                    <option value="marketplace">Marketplace</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="facebook">Facebook</option>
                    <option value="instagram">Instagram</option>
                    <option value="telegram">Telegram</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                {/* Customer Details */}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-slate-700">
                      Customer Information
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowCustomerSelector(true)}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <MagnifyingGlassIcon className="h-4 w-4 mr-1" />
                      Select Customer
                    </button>
                  </div>
                  
                  {orderData.buyer.customerId && (
                    <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-800">
                        ✓ Customer selected: {orderData.buyer.name}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={orderData.buyer.name}
                    onChange={(e) => setOrderData(prev => ({
                      ...prev,
                      buyer: { ...prev.buyer, name: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={orderData.buyer.email}
                    onChange={(e) => setOrderData(prev => ({
                      ...prev,
                      buyer: { ...prev.buyer, email: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    value={orderData.buyer.phone}
                    onChange={(e) => setOrderData(prev => ({
                      ...prev,
                      buyer: { ...prev.buyer, phone: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    value={orderData.buyer.country}
                    onChange={(e) => setOrderData(prev => ({
                      ...prev,
                      buyer: { ...prev.buyer, country: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    State/Province
                  </label>
                  <input
                    type="text"
                    value={orderData.buyer.state}
                    onChange={(e) => setOrderData(prev => ({
                      ...prev,
                      buyer: { ...prev.buyer, state: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={orderData.buyer.city}
                    onChange={(e) => setOrderData(prev => ({
                      ...prev,
                      buyer: { ...prev.buyer, city: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    value={orderData.buyer.postalCode}
                    onChange={(e) => setOrderData(prev => ({
                      ...prev,
                      buyer: { ...prev.buyer, postalCode: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Address
                  </label>
                  <textarea
                    value={orderData.buyer.address}
                    onChange={(e) => setOrderData(prev => ({
                      ...prev,
                      buyer: { ...prev.buyer, address: e.target.value }
                    }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="mt-6">
                <h3 className="text-md font-medium text-slate-900 mb-4">Notes</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Buyer Notes
                    </label>
                    <textarea
                      value={orderData.notes?.buyerNotes || ''}
                      onChange={(e) => setOrderData(prev => ({
                        ...prev,
                        notes: { ...prev.notes, buyerNotes: e.target.value }
                      }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Notes for the buyer..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Internal Notes
                    </label>
                    <textarea
                      value={orderData.notes?.internalNotes || ''}
                      onChange={(e) => setOrderData(prev => ({
                        ...prev,
                        notes: { ...prev.notes, internalNotes: e.target.value }
                      }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Internal notes..."
                    />
                  </div>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate('/marketplace/orders')}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || selectedBirds.length === 0}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Completing Order...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-4 w-4 mr-2" />
                      Complete Order
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Customer Selector Modal */}
        <BrandModal
          open={showCustomerSelector}
          onClose={() => setShowCustomerSelector(false)}
          maxWidthClass="max-w-2xl"
        >
          <BrandModalHeader
            title="Select Customer"
            onClose={() => setShowCustomerSelector(false)}
          />
          <BrandModalBody>
              {/* Search */}
              <div className="mb-4">
                <div className="flex items-center w-full rounded-md border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
                    <MagnifyingGlassIcon className="h-4 w-4" />
                  </span>
                  <input
                    type="text"
                    value={customerSearchTerm}
                    onChange={(e) => setCustomerSearchTerm(e.target.value)}
                    placeholder="Search customers..."
                    className="flex-1 min-w-0 pr-4 py-2 border-0 bg-transparent focus:outline-none focus:ring-0"
                  />
                </div>
              </div>

              {/* Customer List */}
              <div className="max-h-96 overflow-y-auto">
                {loadingCustomers ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {customers
                      .filter(customer => 
                        customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
                        customer.email?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
                        customer.phone?.includes(customerSearchTerm)
                      )
                      .map((customer) => (
                        <div
                          key={customer.id}
                          onClick={() => selectCustomer(customer)}
                          className="p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-slate-50/80 hover:border-blue-300 transition-colors"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium text-slate-900">{customer.name}</h4>
                              <p className="text-sm text-slate-500">{customer.email}</p>
                              <p className="text-sm text-slate-500">{customer.phone}</p>
                              {customer.metadata?.totalPurchases && (
                                <p className="text-xs text-green-600">
                                  {customer.metadata.totalPurchases} previous purchases
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                customer.status === CustomerStatus.ACTIVE 
                                  ? 'bg-emerald-100 text-emerald-800' 
                                  : 'bg-violet-100 text-violet-800'
                              }`}>
                                {customer.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
          </BrandModalBody>
          <BrandModalFooter>
            <button
              type="button"
              onClick={() => setShowCustomerSelector(false)}
              className="btn btn-secondary text-sm"
            >
              Close
            </button>
          </BrandModalFooter>
        </BrandModal>
      </div>
    </div>
  );
}
